import { Button } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import styles from "./ContextSensitiveButton.module.css";
import { useButtonBehavior } from "./hooks";
import type { ContextSensitiveButtonProps } from "./types";

/**
 * How long the context-sensitive button entrance/exit animation lasts.
 *
 * The button entrance is a CSS animation, which can't easily be started halfway through,
 * so to avoid any jumps in the UI, use this duration to debounce any new transitions
 * until the pending animation is complete.
 *
 * NOTE: Must match the values in ContextSensitiveButton.module.css!
 */
const BUTTON_FADE_ANIMATION_DURATION_MS = 100;

/**
 * How long to wait after the host becomes idle before showing the context-sensitive button.
 */
const BUTTON_SHOW_DELAY_MS = 600;

/**
 * A helper button that suggests the viewport mutation we want the user to have available depending on what's going on.
 */
export function ContextSensitiveButton({
  state,
  plotManager,
  localViewport,
  chartExtents,
  isHostHovered,
  isHostIdle,
}: ContextSensitiveButtonProps) {
  const { text, onClick, hasBehavior } = useButtonBehavior({
    state,
    plotManager,
    chartExtents,
    localViewport,
    isHostHovered,
    isHostIdle,
  });

  // The button can be shown when the context has a defined behavior,
  // the host is hovered by the mouse, and the host is not busy with other interactions.
  const canShow = isHostHovered && isHostIdle && hasBehavior;
  const showTimeoutRef = React.useRef<number>();
  const wasHostIdleRef = React.useRef(false);
  const [show, setShow] = React.useState(false);
  const canShowChangedAt = React.useRef(performance.now());
  React.useEffect(
    function showWithDelay() {
      function dispose() {
        window.clearTimeout(showTimeoutRef.current);
      }

      const setShowDelay = (() => {
        if (canShow && !wasHostIdleRef.current) {
          // If the panel was interacting (the host idle state changed from not idle to idle),
          // give a longer wait before showing the button, so the user doesn't see the button
          // aggressively toggle while they are panning or zooming.
          return BUTTON_SHOW_DELAY_MS;
        }
        // At a minimum, wait long enough before changing the state that any pending
        // css animations of the button have completed.
        const fadeElapsed = performance.now() - canShowChangedAt.current;
        return Math.max(0, BUTTON_FADE_ANIMATION_DURATION_MS - fadeElapsed);
      })();

      showTimeoutRef.current = window.setTimeout(function setShowCallback() {
        setShow(canShow);
        canShowChangedAt.current = performance.now();
      }, setShowDelay);
      wasHostIdleRef.current = isHostIdle;
      return dispose;
    },
    [setShow, canShow, isHostIdle],
  );

  // When the button first mounts, it is already faded out.
  // CSS animations don't easily start from the current state, so applying the 'hide'
  // animation on a newly-mounted button would cause it to jump to opacity 1
  // and then fade out again.
  //
  // Therefore, don't use any animation classes until the first time the button has to show itself.
  const canAnimate = React.useRef(false);
  canAnimate.current = canAnimate.current || show;

  const buttonClasses = [styles.button];
  if (canAnimate.current) {
    buttonClasses.push(show ? styles.show : styles.hide);
  }

  return (
    <Button
      className={classNames(...buttonClasses)}
      variant={"outlined"}
      size="small"
      onClick={onClick}
      sx={{
        marginBottom: `${chartExtents?.pixel?.bottom ?? 0}px`,
        marginRight: `${chartExtents?.pixel?.right ?? 0}px`,
      }}
    >
      {text}
    </Button>
  );
}
